<template>
	<b-modal ref="modal" hide-footer size="xl">
		<template v-slot:modal-title>{{ $t('invoice.sepa_history.detail_titre') }}</template>

		<LoadingSpinner v-if="loading" />

		<template v-else-if="transfer">
			<div class="row mb-3">
				<div class="col-12 col-md-6">
					<p class="mb-1"><b>{{ $t('invoice.sepa_history.msg_id') }} :</b> {{ transfer.sepatransfer_msg_id || '-' }}</p>
					<p class="mb-1"><b>{{ $t('invoice.sepa_history.genere_le') }} :</b> {{ transfer.sepatransfer_generated_at_format }}</p>
					<p class="mb-1"><b>{{ $t('invoice.sepa_transfer.date_execution') }} :</b> {{ transfer.sepatransfer_execution_date_format || '-' }}</p>
					<p class="mb-1"><b>{{ $t('invoice.sepa_history.statut_label') }} :</b> {{ $t('invoice.sepa_history.statut.' + transfer.sepatransfer_status) }}</p>
				</div>
				<div class="col-12 col-md-6">
					<p v-if="transfer.accounting_plan" class="mb-1"><b>{{ $t('invoice.sepa_history.entite') }} :</b> {{ transfer.accounting_plan.tiers_rs || transfer.accounting_plan.accountingplan_label }}</p>
					<p v-if="transfer.debtor_account" class="mb-1"><b>{{ $t('invoice.sepa_transfer.compte_debite') }} :</b> {{ transfer.debtor_account.checkdepositconfiguration_libelle }} ({{ transfer.debtor_account.iban }})</p>
					<p class="mb-1"><b>{{ $t('invoice.sepa_history.virements') }} :</b> {{ transfer.sepatransfer_nb_tx }}</p>
					<p class="mb-1"><b>{{ $t('invoice.sepa_transfer.montant') }} :</b> {{ total_format }}</p>
				</div>
			</div>

			<div class="mb-3">
				<button v-if="transfer.has_file" class="btn btn-secondary btn-rounded" @click="downloadSepaTransferXml(transfer.sepatransfer_id)">
					{{ $t('invoice.sepa_history.telecharger') }} <font-awesome-icon :icon="['fal', 'download']"/>
				</button>
				<b-alert v-else show variant="info">{{ $t('invoice.sepa_history.pas_de_fichier') }}</b-alert>
			</div>

			<h3>{{ $t('invoice.sepa_history.rapprochement') }}</h3>

			<template v-if="transfer.bank_transaction">
				<b-alert show variant="success">
					{{ $t('invoice.sepa_history.transaction_rapprochee') }}
					{{ transfer.bank_transaction.bridgetransaction_date_format }} — {{ transfer.bank_transaction.bridgetransaction_description }}
				</b-alert>
				<button class="btn btn-secondary btn-rounded" :disabled="processing" @click="unassign">
					{{ $t('invoice.sepa_history.defaire') }}
					<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse/>
				</button>
			</template>
			<b-alert v-else-if="transfer.sepatransfer_status === 'reconciled'" show variant="success">{{ $t('invoice.sepa_history.rapproche_par_virement') }}</b-alert>
			<b-alert v-else-if="transfer.sepatransfer_status === 'generated'" show variant="warning">{{ $t('invoice.sepa_history.non_confirme') }}</b-alert>
			<b-alert v-else-if="transfer.sepatransfer_status === 'cancelled'" show variant="secondary">{{ $t('invoice.sepa_history.annule') }}</b-alert>
			<template v-else>
				<p v-if="transactions.length === 0">{{ $t('invoice.sepa_history.aucune_transaction', { total: total_format }) }}</p>
				<template v-else>
					<b-form-radio-group v-model="transaction_id" stacked>
						<b-form-radio v-for="transaction in transactions" :key="transaction.bridgetransaction_id" :value="transaction.bridgetransaction_id">
							{{ transaction.bridgetransaction_date_format }} — {{ transaction.bridgetransaction_description }} ({{ transaction.account_name }})
						</b-form-radio>
					</b-form-radio-group>
					<button class="btn btn-primary btn-rounded mt-2" :disabled="processing || !transaction_id" @click="assign">
						{{ $t('invoice.sepa_history.rapprocher') }}
						<font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" pulse/>
					</button>
				</template>
			</template>

			<h3 class="mt-4">{{ $t('invoice.sepa_history.virements') }}</h3>
			<table class="table table-sm">
				<thead>
					<tr>
						<th>{{ $t('invoice.sepa_transfer.fournisseur') }}</th>
						<th>{{ $t('invoice.sepa_transfer.facture') }}</th>
						<th>{{ $t('invoice.sepa_transfer.compte_credite') }}</th>
						<th>{{ $t('invoice.sepa_history.reference') }}</th>
						<th class="text-right">{{ $t('invoice.sepa_transfer.montant') }}</th>
						<th>{{ $t('invoice.sepa_history.reglement') }}</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="line in lines" :key="line.sepatransferline_id">
						<td>{{ line.tiers_rs }}</td>
						<td>
							<router-link :to="{ name: 'invoiceSupplierFicheOverview', params: { invoicesupplier_id: line.suppliersocr_id } }">{{ line.suppliersocr_num }}</router-link>
						</td>
						<td>{{ line.creditor_iban || '-' }}</td>
						<td>{{ line.sepatransferline_sepa_ref || '-' }}</td>
						<td class="text-right">{{ line.amount_format }}</td>
						<td>
							<template v-if="line.ispayment_id">
								{{ line.ispayment_date_format }}
								<font-awesome-icon v-if="line.payment_reconciled" :icon="['fal', 'check']" :title="$t('invoice.sepa_history.reglement_rapproche')"/>
							</template>
							<template v-else>-</template>
						</td>
					</tr>
				</tbody>
			</table>
		</template>
	</b-modal>
</template>


<script type="text/javascript">
	import Invoice from "@/mixins/Invoice.js"

	export default {
		name: "InvoiceSupplierSepaTransferDetail",
		mixins: [Invoice],
		data () {
			return {
				loading: false,
				processing: false,
				transfer: null,
				lines: [],
				total_format: '',
				transactions: [],
				transaction_id: null
			}
		},
		methods: {
			/**
			 * Ouvre le détail d'un lot.
			 *
			 * @param {Number} sepatransfer_id
			 */
			async open(sepatransfer_id) {
				this.transfer = null
				this.transactions = []
				this.transaction_id = null
				this.$refs.modal.show()

				await this.load(sepatransfer_id)
			},

			/**
			 * Recharge le lot et, s'il attend sa remise, les débits qui peuvent l'être.
			 *
			 * @param {Number} sepatransfer_id
			 */
			async load(sepatransfer_id) {
				this.loading = true

				const transfer = await this.getSepaTransfer(sepatransfer_id)

				if(!transfer) {
					this.loading = false
					return
				}

				this.total_format = await this.priceFormat(transfer.sepatransfer_ctrl_sum, 'EUR', true)
				this.lines = await Promise.all(transfer.lines.map(async line => ({
					...line,
					amount_format: await this.priceFormat(line.sepatransferline_amount, 'EUR', true)
				})))

				// Seul un lot confirmé et pas encore rapproché attend sa remise.
				this.transactions = transfer.sepatransfer_status === 'confirmed' ? await this.getSepaTransferTransactions(sepatransfer_id) : []
				this.transaction_id = this.transactions.length === 1 ? this.transactions[0].bridgetransaction_id : null
				this.transfer = transfer

				this.loading = false
			},

			async assign() {
				this.processing = true
				const transfer = await this.assignSepaTransfer(this.transfer.sepatransfer_id, this.transaction_id)
				this.processing = false

				if(transfer) {
					await this.load(this.transfer.sepatransfer_id)
					this.$emit('change')
				}
			},

			async unassign() {
				this.processing = true
				const transfer = await this.unassignSepaTransfer(this.transfer.sepatransfer_id)
				this.processing = false

				if(transfer) {
					await this.load(this.transfer.sepatransfer_id)
					this.$emit('change')
				}
			}
		},
		components: {
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
		}
	}
</script>
